import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Shield, Globe, CreditCard, ChevronRight, ChevronLeft, Landmark } from "lucide-react";
import { cn } from "../lib/utils";

const slides = [
  {
    icon: Shield,
    title: "Secure Digital Banking",
    description: "Experience banking with military-grade encryption. Your money is safe with us.",
    gradient: "from-blue-600 to-blue-800",
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=600",
    bgIcon: Shield
  },
  {
    icon: Globe,
    title: "Fast Global Transfers",
    description: "Send money locally and internationally with lightning speed and low fees.",
    gradient: "from-blue-700 to-indigo-800",
    image: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&q=80&w=600",
    bgIcon: Globe
  },
  {
    icon: CreditCard,
    title: "Cards & Loans",
    description: "Manage your debit cards and apply for instant loans—all in one place.",
    gradient: "from-blue-800 to-slate-900",
    image: "https://images.unsplash.com/photo-1556742044-3c52d6e88c62?auto=format&fit=crop&q=80&w=600",
    bgIcon: CreditCard
  },
];

export const Onboarding = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) setCurrentSlide((prev) => prev + 1);
  };

  const prevSlide = () => {
    if (currentSlide > 0) setCurrentSlide((prev) => prev - 1);
  };

  const CurrentBgIcon = slides[currentSlide].bgIcon;

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center font-sans p-0 md:p-4">
      
      {/* Mobile Container */}
      <div className="w-full max-w-[430px] h-screen md:h-[850px] bg-white md:rounded-[3rem] md:shadow-2xl overflow-hidden flex flex-col relative border-slate-200/50 border">
        
        {/* FAINTED BACKGROUND ICON */}
        <div className="absolute -top-10 -right-10 pointer-events-none transition-all duration-700 ease-in-out transform rotate-12">
          <CurrentBgIcon className="w-64 h-64 text-blue-50 opacity-[0.05]" />
        </div>

        {/* Header */}
        <header className="flex items-center justify-between p-6 pt-10 z-20">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-lg shadow-blue-200">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight text-slate-800">HaysPrime</span>
          </div>
          <Button
            variant="ghost"
            className="text-slate-400 hover:text-blue-600 font-semibold"
            onClick={() => navigate("/login")}
          >
            Skip
          </Button>
        </header>

        {/* Slides Content */}
        <div className="flex-1 flex flex-col items-center justify-center z-10">
          <div className="relative w-full overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out" 
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div key={index} className="w-full flex-shrink-0 flex flex-col items-center px-8">
                  
                  {/* SLIDE IMAGE CONTAINER */}
                  <div className="w-full aspect-square max-w-[280px] mb-10 relative">
                    <div className={cn(
                      "absolute inset-0 rounded-[3rem] rotate-6 opacity-10 bg-gradient-to-br",
                      slide.gradient
                    )} />
                    <img 
                      src={slide.image} 
                      alt={slide.title}
                      className="w-full h-full object-cover rounded-[2.5rem] shadow-2xl relative z-10 border-4 border-white"
                    />
                    <div className={cn(
                      "absolute -bottom-4 -right-4 w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl z-20 bg-gradient-to-br",
                      slide.gradient
                    )}>
                      <slide.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  <h2 className="text-2xl font-extrabold mb-3 text-slate-900 tracking-tight">
                    {slide.title}
                  </h2>
                  <p className="text-slate-500 text-base leading-relaxed max-w-[300px] text-center">
                    {slide.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination Dots */}
          <div className="flex gap-2 mt-10">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-500",
                  index === currentSlide ? "bg-blue-600 w-10" : "bg-slate-200 w-2"
                )}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="p-8 space-y-4 pb-12 z-20">
          {currentSlide < slides.length - 1 ? (
             <Button
               onClick={nextSlide}
               className="w-full h-14 text-base font-bold bg-slate-900 hover:bg-black text-white rounded-2xl transition-all shadow-xl shadow-slate-200"
             >
               Next Step
               <ChevronRight className="w-4 h-4 ml-2" />
             </Button>
          ) : (
            <Button
              onClick={() => navigate("/signup")}
              className="w-full h-14 text-base font-bold bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white rounded-2xl shadow-xl shadow-blue-200 border-none transition-all scale-100 active:scale-95"
            >
              Create Free Account
            </Button>
          )}
          
          <Button
            variant="ghost"
            onClick={() => navigate("/login")}
            className="w-full h-14 text-base font-bold text-primary hover:bg-slate-50 rounded-2xl"
          >
            Log In to Account
          </Button>
        </footer>
      </div>
    </div>
  );
};